import { useState, useEffect, useRef } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SendButton } from "@/components/SendButton";
import { useAgentRulesStore } from "@/store/agentRulesStore";
import { useAuthStore, useHasPermission } from "@/store/authStore";
import { AGENT_RULE_ACTOR_TYPE, Permission } from "@forgepoint/shared";

/**
 * A floating action button (FAB) positioned in the bottom-right corner
 * that expands into a textarea to add a new shop rule.
 * Closes when clicking outside the expanded box.
 */
export function AddShopRuleButton() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [content, setContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const createRule = useAgentRulesStore((state) => state.createRule);
  const user = useAuthStore((state) => state.user);
  const canEdit = useHasPermission(Permission.SHOPRULES_EDIT);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isExpanded &&
        !isSubmitting &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setContent("");
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isExpanded, isSubmitting]);

  if (!canEdit) {
    return null;
  }

  const handleSubmit = async () => {
    if (!content.trim() || !user) {
      return;
    }

    setIsSubmitting(true);

    const result = await createRule(
      content,
      user.id,
      AGENT_RULE_ACTOR_TYPE.USER,
    );

    if (result) {
      setContent("");
      setIsExpanded(false);
    }

    setIsSubmitting(false);
  };

  if (isExpanded) {
    return (
      <div
        ref={containerRef}
        className="fixed bottom-6 right-6 w-80 bg-background border rounded-lg shadow-lg p-4 animate-in fade-in slide-in-from-bottom-2 duration-200 pointer-events-auto"
        style={{ zIndex: 1000 }}
      >
        <div className="relative">
          <Textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onSend={handleSubmit}
            placeholder="Enter your shop rule here..."
            disabled={isSubmitting}
            className="min-h-[100px] max-h-60 overflow-y-auto pr-12 pb-10"
            minRows={4}
            maxRows={10}
          />
          <SendButton
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting}
            loading={isSubmitting}
            iconSize={12}
            className="absolute bottom-2 right-2 h-6 w-6 rounded-full"
          />
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="icon"
            onClick={() => setIsExpanded(true)}
            className="fixed bottom-24 right-6 h-10 w-10 rounded-full shadow-lg hover:shadow-xl transition-shadow"
            aria-label="Add shop rule"
            style={{ zIndex: 1000 }}
          >
            <Plus className="h-5 w-5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Shop Rules</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
